import type { MccTierValue } from "@cpos/shared";
import type { TermSplit } from "@/application/signals/types";

export function formatPercent(share: number): string {
  return `${(share * 100).toFixed(share < 0.1 ? 1 : 0)}%`;
}

/**
 * Formats minor units in their own currency. The merchant base settles in more
 * than one, so the code is always required rather than assumed to be USD.
 */
export function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Renders a per-currency GPV total. Currencies are listed separately, largest
 * first — converting between them would need a rate the artifact does not carry.
 */
export function formatGpv(gpvByCurrency: Record<string, number>): string {
  const entries = Object.entries(gpvByCurrency)
    .filter(([, cents]) => cents > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return "—";
  return entries
    .map(
      ([currency, cents]) => `${formatCurrency(cents, currency)} ${currency}`,
    )
    .join(" · ");
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatDays(days: number | null): string {
  if (days === null) return "—";
  if (days < 1) return "same day";
  return `${days.toFixed(days < 10 ? 1 : 0)}d`;
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** `2026-01` → `Jan '26`. The year is kept so a multi-year series stays readable. */
export function formatMonth(month: string): string {
  const [year, m] = month.split("-");
  const date = new Date(Number(year), Number(m) - 1, 1);
  const label = date.toLocaleDateString("en-US", { month: "short" });
  return `${label} '${year.slice(2)}`;
}

const STAGE_LABELS = new Map<string, string>([
  ["active_no_transaction", "Active, never transacted"],
  ["onboarding", "Onboarding"],
  ["suspended", "Suspended"],
  ["closed", "Closed"],
  ["unknown", "Unknown stage"],
]);

/**
 * Renders a stall stage for display. Stages are namespaced (`identity:failed`,
 * `subscription:past_due`) so the prefix names the gate the merchant is stuck
 * behind.
 */
export function formatStallStage(stage: string): string {
  const known = STAGE_LABELS.get(stage);
  if (known) return known;
  const [prefix, rest] = stage.split(":");
  if (!rest) return titleCase(prefix);
  const gate = prefix === "identity" ? "Identity" : "Subscription";
  return `${gate}: ${titleCase(rest)}`;
}

/** Compact tier badge. Tiers are numeric except the first-class unclassified bucket. */
export function formatTier(tier: MccTierValue): string {
  return tier === "unclassified" ? "Unclassified" : `T${tier}`;
}

export function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const CHANNEL_LABELS = new Map<string, string>([
  ["paid", "Paid"],
  ["social", "Social"],
  ["organic", "Organic"],
  ["email", "Email"],
  ["referral", "Referral"],
  ["direct", "Direct"],
  ["other", "Other"],
  ["unknown", "No attribution"],
]);

export function formatChannel(channel: string): string {
  return CHANNEL_LABELS.get(channel) ?? titleCase(channel);
}

const TERM_LABELS = new Map<string, string>([
  ["monthly", "Monthly"],
  ["annual", "Annual"],
  ["unknown", "Unknown"],
]);

/** Renders a Pro merchant's billing term; anything unresolved reads `Unknown`. */
export function formatTerm(term: string | null | undefined): string {
  return TERM_LABELS.get(term ?? "unknown") ?? "Unknown";
}

/** `12 monthly · 3 annual`, with `· N unknown` only when some term is unresolved. */
export function formatTermSplit(split: TermSplit): string {
  const parts = [
    `${formatCount(split.monthly)} monthly`,
    `${formatCount(split.annual)} annual`,
  ];
  if (split.unknown > 0) {
    parts.push(`${formatCount(split.unknown)} unknown`);
  }
  return parts.join(" · ");
}

const COUNTRY_LABELS = new Map<string, string>([
  ["US", "United States"],
  ["CA", "Canada"],
  ["AU", "Australia"],
  ["unknown", "Unknown"],
]);

export function formatCountry(code: string): string {
  return COUNTRY_LABELS.get(code) ?? code;
}
